import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { forkJoin, merge, Observable, of, Subscription } from 'rxjs';
import {
  catchError,
  defaultIfEmpty,
  flatMap,
  map,
  mapTo,
  share
} from 'rxjs/operators';
import { reportError } from '../error-classification';
import { ClaimsService } from '@core/authentication/claims.service';

type CommandEventType = 'Initiation' | 'Result' | 'Failure';

export type CommandEvent<T> = {
  type: CommandEventType;
  result: T;
  error: any | null;
};

const commandInitiation: CommandEvent<any> = {
  type: 'Initiation',
  result: null,
  error: null
};

function commandResult<T>(result: T): CommandEvent<T> {
  return {
    type: 'Result',
    result: result,
    error: null
  };
}

function commandFailure(error: any): CommandEvent<any> {
  return {
    type: 'Failure',
    result: null,
    error: error
  };
}

function combineEvents<V>(events: CommandEvent<V>[]): CommandEvent<V[]> {
  return events.some((x) => x.type == 'Failure')
    ? commandFailure(events.find((x) => x.type == 'Failure'))
    : commandResult(
        events.filter((x) => x.type == 'Result').map((x) => x.result)
      );
}

function wrapEvent<V>(command: Observable<V>): Observable<CommandEvent<V>> {
  return command.pipe(
    map(commandResult),
    catchError((error) => of(commandFailure(error)))
  );
}

export function commandEvent<T, U>(
  trigger$: Observable<T>,
  command: (x: T) => Observable<U>
): Observable<CommandEvent<U>> {
  const sharedTrigger$: Observable<T> = trigger$.pipe(share());
  const initiation$: Observable<CommandEvent<U>> = sharedTrigger$.pipe(
    mapTo(commandInitiation)
  );
  const outcome$: Observable<CommandEvent<U>> = sharedTrigger$.pipe(
    flatMap((x) => wrapEvent(command(x)))
  );
  return merge(initiation$, outcome$);
}

export function commandsEvent<T, U>(
  trigger$: Observable<T>,
  commands: (x: T) => Observable<U>[]
): Observable<CommandEvent<U[]>> {
  const sharedTrigger$: Observable<T> = trigger$.pipe(share());
  const initiation$: Observable<CommandEvent<U[]>> = sharedTrigger$.pipe(
    mapTo(commandInitiation)
  );
  const outcomes$: Observable<CommandEvent<U>[]> = sharedTrigger$.pipe(
    flatMap((x) =>
      forkJoin(commands(x).map(wrapEvent)).pipe(
        defaultIfEmpty<CommandEvent<U>[], CommandEvent<U>[]>([])
      )
    )
  );
  const outcome$: Observable<CommandEvent<U[]>> = outcomes$.pipe(
    map(combineEvents)
  );
  return merge(initiation$, outcome$);
}

type CommandState = 'Ready' | 'InProgress';

// The CommandComponent manages the execution of commands to make changes to the data, or execute actions. It
// encapsulates several common concerns, including execution of the command, presentation of an "in progress" indicator,
// and feedback about the result of the command.
//
// The CommandComponent is used by surrounding the elements of the template to which the command applies (perhaps an
// entire form, or maybe just one button, as appropriate). Usage is straight-forward. For example:
//
//   <app-command [command]="save$" (success)="redirect()">
//     <form (ngSubmit)="save()">
//       ...
//     </form>
//   </app-command>
//
// The CommandComponent requires a definition of the command to be executed, in the form of an Observable<T> (actually
// an Observable<CommandEvent<T>>, but more on that later). The component handles the rest: subscription to the
// observable, "in progress" indicator, and outcome feedback. While the command is being executed, the content
// contained within the component in the template will be inactive, and the "in progress" indicator will appear over the
// top of it.
//
// A consumer may subscribe to the success event, which will be emitted each time the command completes successfully.
// The value produced by the successful command will be provided with the success event. This provides the ability to
// perform additional behaviour on successful completion of the command, such as a redirection. This has been subsequently
// updated to allow for a Null to be emitted as certain operations such as DELETE requests will only return an OK 200 response
// without a result value (payload).
//
// Internally, the CommandComponent depends on a CommandEvent<T> type, which represents the various events which can
// occur during command processing (initiation, successful completion, and failure). The observable which the
// CommandComponent requires must actually be of type Observable<CommandEvent<T>>. A convenience function, commandEvent,
// is provided to assist with construction of such an observable. It takes two arguments:
//
//  - An observable of command initiations (typically generated from the clicking of a button, or selection of a menu
//    item).
//  - A function which, given a value produced by the command initiations observable supplied in the first argument,
//    returns an observable definition of the command to be executed.
//
// The commandEvent function combines these two arguments into an appropriate Observable<CommandEvent<T>>, suitable for
// consumption by the CommandComponent. So for example, a component may define a save command as follows:
//
//   private saveRequest$: Subject<SaveRequest> = new Subject<SaveRequest>()
//   save$: Observable<CommandEvent<SaveResponse>> = commandEvent(
//     this.saveRequest$,
//     saveRequest => this.service.save(saveRequest))
//
//   save(): void {
//     this.saveRequest$.next(new SaveRequest(this.form.value))
//   }
//
// In some cases, a command action may actually be comprised of multiple actions. For this circumstance, the
// commandsEvent function provides a similar service to the commandEvent function, the difference being that the
// function argument may return an array of observables defining the actions to be performed. For example:
//
//   private saveRequest$: Subject<SaveRequest> = new Subject<SaveRequest>()
//   save$: Observable<CommandEvent<SaveResponse>> = commandsEvent(
//     this.saveRequest$,
//     saveRequest => [
//       this.service.savePartA(saveRequest.partA),
//       this.service.savePartB(saveRequest.partB),
//       this.service.savePartC(saveRequest.partC),
//     ])
//
//   save(): void {
//     this.saveRequest$.next(new SaveRequest(this.form.value))
//   }
//
// Note that in this case, if any of the actions fail, the overall command will fail, even though some of the actions
// may in fact have succeeded. Depending on the nature and behaviour of the components involved, this may result in the
// user clicking a button to try again, which would repeat all of the actions, not just the failed ones. To avoid this,
// it may be wise for the function argument to commandsEvent to conditionally include each action as appropriate. For
// example, the dirty state of the form fields could be used to determine whether each action should be performed. This
// also helps to avoid unnecessary actions when the user does not make a change to one section of a form. Each action
// may then mark the relevant fields as pristine as a side-effect of successful completion, so that repeated attempts
// will not retry the same actions which already succeeded. For example:
//
//   save$: Observable<CommandEvent<SaveResponse>> = commandsEvent(
//     this.saveRequest$,
//     saveRequest => {
//       const actions = []
//       if (this.partA.dirty)
//       {
//         actions.push(this.service.savePartA(saveRequest.partA).pipe(tap(_ => this.partA.markAsPristine())))
//       }
//       if (this.partB.dirty)
//       {
//         actions.push(this.service.savePartB(saveRequest.partB).pipe(tap(_ => this.partB.markAsPristine())))
//       }
//       return actions
//     ]})

@Component({
  selector: 'app-command',
  templateUrl: './command.component.html',
  styleUrls: ['./command.component.scss']
})
export class CommandComponent<T> implements OnDestroy {
  private subscription: Subscription | null = null;
  private modal: BsModalRef | null = null;

  @Input() setOwnErrorFeedback: boolean = false;

  @ViewChild('failure', { read: TemplateRef })
  failureTemplate!: TemplateRef<any>;

  @Input() set command(observable: Observable<CommandEvent<T>>) {
    this.subscription = observable.subscribe(
      (event) => {
        switch (event.type) {
          case 'Initiation':
            this.inProgress();
            break;
          case 'Result':
            if (event.error == null) {
              this.complete(event.result);
            } else {
              this.failure(
                'Design violation: CommandEvent of type Result has an error value of - ' +
                  event.error
              );
            }
            break;
          case 'Failure':
            this.failure(event.error);
            break;
        }
      },
      (error) => this.failure(error)
    );
  }

  @Output() success: EventEmitter<T> = new EventEmitter<T>();
  @Output() fail: EventEmitter<T> = new EventEmitter<T>();

  state: CommandState = 'Ready';
  error: any = null;

  constructor(private modalService: BsModalService) {}

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  closeModal(): void {
    this.modal?.hide();
  }

  private inProgress(): void {
    this.state = 'InProgress';
  }

  private complete(value: T): void {
    this.success.emit(value);
    this.state = 'Ready';
  }

  private failure(error: any): void {
    reportError(error);
    this.error = error;
    this.fail.emit(error);
    if (!this.setOwnErrorFeedback) {
      this.modal = this.modalService.show(this.failureTemplate);
    }

    this.state = 'Ready';
  }
}
